import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useCompany } from '../../../context/CompanyContext';

/**
 * Custom hook for fetching a single client from Firestore
 * Read-only hook for client profile
 */
export const useClient = (clientId) => {
  const { companyId } = useCompany();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    if (!db || !companyId) {
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setNotFound(false);

    try {
      const clientRef = doc(db, 'companies', companyId, 'leads', clientId);

      const unsubscribe = onSnapshot(
        clientRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            
            // Check if client is soft-deleted
            if (data.deleted === true) {
              setClient(null);
              setNotFound(true);
              setLoading(false);
              return;
            }
            
            // Normalize client data
            setClient({
              id: docSnap.id,
              name: data.name || 'Unnamed Client',
              email: data.email || '',
              phone: data.phone || '',
              address: normalizeAddress(data.address),
              stage: data.stage || 'New Lead',
              source: data.source || '',
              notes: data.notes || '',
              createdAt: data.createdAt || null,
              updatedAt: data.updatedAt || null,
              deleted: data.deleted || false,
              deletedAt: data.deletedAt || null,
              ...data,
            });
            setNotFound(false);
            setLoading(false);
          } else {
            setClient(null);
            setNotFound(true);
            setLoading(false);
          }
        },
        (err) => {
          console.error('Error fetching client:', err);
          setError(err.message || 'Failed to fetch client');
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up client listener:', err);
      setError(err.message || 'Failed to initialize client listener');
      setLoading(false);
    }
  }, [companyId, clientId]);

  return { client, loading, error, notFound };
};

/**
 * Helper function to normalize address field
 */
function normalizeAddress(address) {
  if (!address) return '';
  
  if (typeof address === 'string') {
    return address;
  }
  
  if (typeof address === 'object') {
    const { streetNumber, streetName, street, city, state, zip } = address;
    const streetPart = street || `${streetNumber || ''} ${streetName || ''}`.trim();
    const parts = [streetPart, city, state, zip].filter(Boolean);
    return parts.join(', ');
  }
  
  return '';
}
